import React from 'react';
import { measureDistance } from '../utils/measureDistance';
import { usePointerDrag } from './usePointerDrag';
const dragScrollTabDistance = 100;
const dragVerticalBoundary = 50;
const dragScrollInternalTimeout = 50;
const dragScrollSpeed = (distance) => Math.sign(distance) * 10;
/**
 * Custom React hook for enabling pointer-based tab reordering through drag-and-drop within a tab bar.
 * Supports dynamic scroll, visual feedback (drag position and offset), and index-based tab insertion.
 *
 * @param {{tabs:GlobalTab[], onChangeTabs:(tabs:GlobalTab[])=>void}} props - Object containing:
 *   - `tabs`: The current list of tabs (with path keys).
 *   - `onChangeTabs`: Callback to update the tab order when reordering completes.
 * @param {React.RefObject<HTMLDivElement | null>} rootRef - Ref to the DOM element containing the tab list (e.g., the tab bar root).
 *
 * @returns {{
 *  dragPoint :Point|undefined,
 *  dragOffset:Point | undefined,
 *  dragMoveToIndex:number|undefined,
 *  dragMoveToRect:DOMRect|undefined,
 *  draggedTab:GlobalTab |undefined,
 *  pointerDragEvents:PointerDragEvents
 * }} An object containing:
 * - `dragPoint`: Current pointer position during drag.
 * - `dragOffset`: Offset from pointer to the top-left corner of the dragged tab.
 * - `dragMoveToIndex`: Index in the tab array where the tab would be moved to.
 * - `dragMoveToRect`: The bounding rect of the insertion point for rendering visual feedback.
 * - `draggedTab`: The currently dragged tab object.
 * - `pointerDragEvents`: Pointer event handlers (`onPointerDown`, `onPointerMove`, `onPointerUp`, `onContextMenu`)
 *   to attach to each draggable tab item.
 *
 * @example
 * const {
 *   dragPoint,
 *   dragOffset,
 *   dragMoveToIndex,
 *   pointerDragEvents
 * } = usePointerDragReorder({ tabs, onChangeTabs }, rootRef);
 *
 * return (
 *   <div ref={rootRef}>
 *     {tabs.map((tab, index) => (
 *       <Tab
 *         key={tab.path}
 *         data-tab-path={tab.path}
 *         data-index={index}
 *         {...pointerDragEvents}
 *       />
 *     ))}
 *   </div>
 * );
 */
export function usePointerDragReorder(props, rootRef) {
    const dragScrollDistanceRef = React.useRef(undefined);
    const [dragPoint, setDragPoint] = React.useState();
    const [dragOffset, setDragOffset] = React.useState();
    const [dragTabPath, setDragTabPath] = React.useState();
    const [dragMoveToIndex, setDragMoveToIndex] = React.useState();
    const [dragMoveToRect, setDragMoveToRect] = React.useState();
    const draggedTabIndex = dragTabPath ? props.tabs.findIndex((tab) => tab.path === dragTabPath) : -1;
    const draggedTab = draggedTabIndex !== -1 ? props.tabs[draggedTabIndex] : undefined;
    const dragScrollIntervalHandleRef = React.useRef(undefined);
    const handleDragMove = React.useCallback((point, dragScrollCallback) => {
        var _a, _b;
        const root = rootRef.current;
        if (root) {
            if (isPointerInBarBoundary(root, point)) {
                const scrollStartRect = (_a = root.querySelector('.juvareGlobalTabScrollStart')) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                const scrollEndRect = (_b = root.querySelector('.juvareGlobalTabScrollEnd')) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
                const distanceToStart = scrollStartRect ? measureDistance(point, scrollStartRect) : dragScrollTabDistance + 1;
                const distanceToEnd = scrollEndRect ? measureDistance(point, scrollEndRect) : dragScrollTabDistance + 1;
                if (distanceToStart <= dragScrollTabDistance && distanceToStart <= distanceToEnd) {
                    dragScrollDistanceRef.current = -distanceToStart;
                    if (!dragScrollIntervalHandleRef.current) {
                        dragScrollIntervalHandleRef.current = window.setInterval(dragScrollCallback, dragScrollInternalTimeout);
                    }
                }
                else if (distanceToEnd <= dragScrollTabDistance && distanceToEnd <= distanceToStart) {
                    dragScrollDistanceRef.current = distanceToEnd;
                    if (!dragScrollIntervalHandleRef.current) {
                        dragScrollIntervalHandleRef.current = window.setInterval(dragScrollCallback, dragScrollInternalTimeout);
                    }
                }
                else {
                    dragScrollDistanceRef.current = undefined;
                    if (dragScrollIntervalHandleRef.current) {
                        window.clearInterval(dragScrollIntervalHandleRef.current);
                        dragScrollIntervalHandleRef.current = undefined;
                    }
                }
                const [moveToIndex, moveToRect] = findMoveToIndex(root, point);
                setDragMoveToIndex(moveToIndex < draggedTabIndex || moveToIndex > draggedTabIndex + 1 ? moveToIndex : undefined);
                setDragMoveToRect(moveToIndex < draggedTabIndex || moveToIndex > draggedTabIndex + 1 ? moveToRect : undefined);
            }
            else {
                setDragMoveToIndex(undefined);
                setDragMoveToRect(undefined);
            }
        }
    }, [draggedTabIndex, rootRef]);
    const dragScrollCallback = React.useCallback(() => {
        var _a;
        const distance = dragScrollDistanceRef.current;
        if (distance != null) {
            const tabList = (_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.MuiTabs-scroller');
            tabList.scrollLeft = tabList.scrollLeft + dragScrollSpeed(distance);
            if (dragPoint) {
                handleDragMove(dragPoint, dragScrollCallback);
            }
        }
    }, [rootRef, dragPoint, handleDragMove]);
    const pointerDragEvents = usePointerDrag({
        onDragStart: (point, tabPath, event) => {
            setDragPoint(point);
            setDragTabPath(tabPath);
            const rect = event.currentTarget.getBoundingClientRect();
            setDragOffset({ x: point.x - rect.x, y: point.y - rect.y });
            return true;
        },
        onDragMove: (point, tabPath) => {
            setDragPoint(point);
            setDragTabPath(tabPath);
            handleDragMove(point, dragScrollCallback);
        },
        onDragEnd: (point) => {
            var _a, _b;
            setDragPoint(undefined);
            setDragOffset(undefined);
            setDragTabPath(undefined);
            setDragMoveToIndex(undefined);
            setDragMoveToRect(undefined);
            dragScrollDistanceRef.current = undefined;
            if (dragScrollIntervalHandleRef.current) {
                window.clearInterval(dragScrollIntervalHandleRef.current);
                dragScrollIntervalHandleRef.current = undefined;
            }
            const root = rootRef.current;
            if (draggedTab && root && isPointerInBarBoundary(root, point)) {
                const [moveToIndex] = findMoveToIndex(root, point);
                if (moveToIndex !== -1 && moveToIndex < draggedTabIndex) {
                    const tabs = [...props.tabs];
                    const draggedTab = tabs[draggedTabIndex];
                    tabs.splice(draggedTabIndex, 1);
                    tabs.splice(moveToIndex, 0, draggedTab);
                    (_a = props.onChangeTabs) === null || _a === void 0 ? void 0 : _a.call(props, tabs);
                }
                else if (moveToIndex !== -1 && moveToIndex > draggedTabIndex + 1) {
                    const tabs = [...props.tabs];
                    const draggedTab = tabs[draggedTabIndex];
                    tabs.splice(draggedTabIndex, 1);
                    tabs.splice(moveToIndex - 1, 0, draggedTab);
                    (_b = props.onChangeTabs) === null || _b === void 0 ? void 0 : _b.call(props, tabs);
                }
            }
        },
        onDragCancel: () => {
            setDragPoint(undefined);
            setDragOffset(undefined);
            setDragTabPath(undefined);
            setDragMoveToIndex(undefined);
            setDragMoveToRect(undefined);
            dragScrollDistanceRef.current = undefined;
            if (dragScrollIntervalHandleRef.current) {
                window.clearInterval(dragScrollIntervalHandleRef.current);
                dragScrollIntervalHandleRef.current = undefined;
            }
        },
    }, (event) => ({ x: event.clientX, y: event.clientY }), (event) => {
        return { x: event.clientX, y: event.clientY };
    });
    return { dragPoint, dragOffset, dragMoveToIndex, dragMoveToRect, draggedTab, pointerDragEvents };
}
function findMoveToIndex(root, point) {
    let minSeparatorDistance = Number.MAX_SAFE_INTEGER;
    let moveToIndex = -1;
    let moveToElement;
    for (const separator of root.querySelectorAll('button[disabled]')) {
        const separatorRect = separator.getBoundingClientRect();
        const separatorX = separatorRect.x + separatorRect.width / 2;
        const separatorDistance = Math.abs(separatorX - point.x);
        if (separatorDistance < minSeparatorDistance) {
            minSeparatorDistance = separatorDistance;
            const index = separator.dataset.index;
            if (index) {
                moveToIndex = parseInt(index);
                moveToElement = separator;
            }
        }
    }
    return [moveToIndex, moveToElement === null || moveToElement === void 0 ? void 0 : moveToElement.getBoundingClientRect()];
}
function isPointerInBarBoundary(root, point) {
    const rectRect = root.getBoundingClientRect();
    return point.y >= rectRect.y - dragVerticalBoundary && point.y < rectRect.y + rectRect.height + dragVerticalBoundary;
}
